import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Testids } from '@mono/ui-selectors';
import DOMPurify from 'dompurify';
import { useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
/**
 * The cited words without Markdown syntax. The model quotes from the
 * Markdown it read (`**Scope of Subcontracted Work:**`), while every place
 * that locates the quote searches text without it: rendered source text and
 * the original PDF's text. Left in, the quote never matches (SAD-1343).
 */ export function plainQuote(quote) {
    return quote.replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1') // [text](url), ![alt](src)
    .replace(/\\([\\`*_{}[\]()#+\-.!|>~])/g, '$1') // backslash escapes
    .replace(/^\s{0,3}#{1,6}\s+/gm, '') // heading markers
    .replace(/^\s{0,3}>\s?/gm, '') // blockquote markers
    .replace(/^\s*(?:[-*+]|\d+[.)])\s+/gm, '') // list markers
    .replace(/^\s*\|?(?:\s*:?-{3,}:?\s*\|)+\s*:?-{0,}:?\s*$/gm, '') // table separator rows
    .replace(/\s*\|\s*/g, ' ') // table cell pipes
    .replace(/(\*\*|__|~~)(?=\S)([\s\S]*?\S)\1/g, '$2') // bold, strikethrough
    .replace(/\*\*|__/g, '') // unpaired bold markers (quote cut mid-span)
    .replace(/(^|[^\w*])\*(?=\S)([^*\n]*?\S)\*(?!\w)/g, '$1$2') // *italic*
    .replace(/(^|[^\w])_(?=\S)([^_\n]*?\S)_(?!\w)/g, '$1$2') // _italic_
    .replace(/`+/g, '') // inline code
    .trim();
}
export function normalized(text) {
    let value = '';
    const positions = [];
    for(let i = 0; i < text.length; i++){
        const char = text[i].replace(/[“”]/g, '"').replace(/[‘’]/g, "'").replace(/[–—]/g, '-').toLowerCase();
        if (/\s/.test(char)) {
            if (!value || value.endsWith(' ')) continue;
            value += ' ';
        } else {
            value += char;
        }
        for(let part = 0; part < char.length; part++)positions.push(i);
    }
    return {
        value: value.trimEnd(),
        positions
    };
}
// Match the rendered source text, including quotes crossing emphasis or lines.
export function highlightSourceQuote(quote) {
    return ()=>(tree)=>{
            const texts = [];
            let content = '';
            function collect(node, parent) {
                if (node.type === 'text' && node.value && parent) {
                    texts.push({
                        node,
                        parent,
                        start: content.length
                    });
                    content += node.value;
                }
                for (const child of node.children ?? [])collect(child, node);
                if (node.type === 'element' && /^(p|h[1-6]|li|tr|td|th|br)$/.test(node.tagName ?? '')) content += '\n';
            }
            collect(tree);
            const needle = normalized(quote).value.trim();
            if (!needle) return;
            const source = normalized(content);
            const index = source.value.indexOf(needle);
            if (index < 0) return;
            const start = source.positions[index];
            const end = source.positions[index + needle.length - 1] + 1;
            for (const { node, parent, start: nodeStart } of texts.reverse()){
                const from = Math.max(0, start - nodeStart);
                const to = Math.min(node.value?.length ?? 0, end - nodeStart);
                if (from >= to || !node.value || !parent.children) continue;
                const replacement = [];
                if (from) replacement.push({
                    type: 'text',
                    value: node.value.slice(0, from)
                });
                replacement.push({
                    type: 'element',
                    tagName: 'mark',
                    properties: {
                        'data-citation-highlight': 'true',
                        'data-testid': Testids.assistant.sourceHighlight,
                        className: 'rounded'
                    },
                    children: [
                        {
                            type: 'text',
                            value: node.value.slice(from, to)
                        }
                    ]
                });
                if (to < node.value.length) replacement.push({
                    type: 'text',
                    value: node.value.slice(to)
                });
                parent.children.splice(parent.children.indexOf(node), 1, ...replacement);
            }
        };
}
export function SourceText({ text, quote = '', onLocated }) {
    const root = useRef(null);
    const [found, setFound] = useState(false);
    const safeText = useMemo(()=>// Converter page separators ("{3}------") are not document content.
        DOMPurify.sanitize(text.replace(/^\{\d+\}-{3,}[ \t]*$/gm, ''), {
            ALLOWED_TAGS: [
                'b',
                'strong',
                'i',
                'em',
                'u',
                'br',
                'table',
                'thead',
                'tbody',
                'tr',
                'td',
                'th',
                'p',
                'div',
                'span',
                'ul',
                'ol',
                'li',
                'h1',
                'h2',
                'h3',
                'h4',
                'h5',
                'h6'
            ],
            ALLOWED_ATTR: [
                'colspan',
                'rowspan'
            ]
        }), [
        text
    ]);
    const highlight = useMemo(()=>highlightSourceQuote(quote), [
        quote
    ]);
    useEffect(()=>{
        const match = root.current?.querySelector('mark[data-citation-highlight]');
        setFound(!!match);
        if (quote) onLocated?.(!!match);
        match?.scrollIntoView?.({
            block: 'center'
        });
    }, [
        text,
        quote,
        onLocated
    ]);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            quote && !onLocated ? /*#__PURE__*/ _jsx("output", {
                className: "block text-sm text-muted-foreground",
                children: found ? 'Cited passage highlighted below.' : 'The quoted passage could not be located in this source text.'
            }) : null,
            /*#__PURE__*/ _jsx("div", {
                ref: root,
                className: "prose dark:prose-invert max-w-none break-words",
                children: /*#__PURE__*/ _jsx(ReactMarkdown, {
                    remarkPlugins: [
                        remarkGfm
                    ],
                    rehypePlugins: [
                        rehypeRaw,
                        highlight
                    ],
                    components: {
                        img: ({ alt })=>/*#__PURE__*/ _jsx("span", {
                                children: alt
                            })
                    },
                    children: safeText
                })
            })
        ]
    });
}
